import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IMAGES, STATS, SKOOL_URL } from "@/lib/data";
import { track } from "@/lib/analytics";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden py-[0.05em]">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.58, 0.92]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.heroStudio} alt="Studio console at night" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 z-10 bg-[#050505]" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/70" />

      <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 min-h-[100svh] flex flex-col justify-end pb-14 pt-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="text-[12px] tracking-[0.35em] uppercase text-white/55 mb-6"
        >
          TAPSS — Producer &amp; Creative Partner
        </motion.p>

        <h1 className="font-heading uppercase leading-[0.9] tracking-tight text-white text-5xl sm:text-6xl lg:text-[5.4rem]">
          <MaskLine i={0}>Develop your sound.</MaskLine>
          <MaskLine i={1}>
            Build your <span className="font-serif-accent italic lowercase text-gold normal-case tracking-normal">catalogue</span>.
          </MaskLine>
          <MaskLine i={2}>Grow with direction.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9 }}
          className="mt-8 max-w-xl text-white/60 leading-relaxed text-[15px]"
        >
          I help artists develop a sound, build a catalogue and create music with long-term direction — not one-off beats. This is the home base for the whole TAPSS ecosystem.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          className="mt-9 flex flex-wrap items-center gap-3"
        >
          <a href="#book-call" onClick={() => track("booking_click", "hero")} data-testid="hero-book-cta" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-black text-[13px] tracking-[0.1em] uppercase font-medium hover:-translate-y-1 transition-transform duration-300">
            Book a Call <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
          </a>
          <a href="#partnership" onClick={() => track("partnership_click", "hero")} data-testid="hero-work-with-cta" className="inline-flex items-center px-7 py-3.5 rounded-full border border-white/25 text-white text-[13px] tracking-[0.1em] uppercase hover:bg-white/10 hover:text-white transition-colors duration-300">
            Work With Me
          </a>
          <a href={SKOOL_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("skool_click", "hero")} data-testid="hero-skool-cta" className="inline-flex items-center px-7 py-3.5 rounded-full border border-white/25 text-white text-[13px] tracking-[0.1em] uppercase hover:bg-white/10 hover:text-white transition-colors duration-300">
            Join Skool
          </a>
          <a href="#work" data-testid="hero-explore-cta" className="inline-flex items-center px-7 py-3.5 rounded-full text-white/70 text-[13px] tracking-[0.1em] uppercase hover:text-white transition-colors duration-300">
            Explore My Work
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.45, duration: 1 }}
          className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-xl overflow-hidden"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#050505]/80 px-6 py-5" data-testid={`stat-${s.label}`}>
              <div className="font-heading text-2xl lg:text-3xl text-gold tracking-tight">{s.value}</div>
              <div className="text-[11px] tracking-[0.16em] uppercase text-white/45 mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
